import React, { useEffect, useState } from 'react';

import { MtxButton, MtxGrid, MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { SESSION_EXPIRED_KEY } from '@/api/mutator/sessionExpired';
import { HeadsetIcon } from '@/assets/icons/HeadsetIcon';
import AccessAccordion from '@/components/AccessAccordion/AccessAccordion';
import { AccessCard } from '@/components/AccessCard/AccessCard';
import { LinkCard } from '@/components/LinkCard/LinkCard';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import Toast from '@/components/Toast/Toast';
import { AUTH0_LOGIN_ERROR_KEY } from '@/constants/auth0';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { ROUTE } from '@/constants/route';
import { URLS } from '@/constants/urls';
import useAuth0Login, { trackLoginResult } from '@/hooks/useAuth0Login';
import { useRappelConseiller } from '@/hooks/useRappelConseiller';
import useScreenSize from '@/hooks/useScreenSize';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { ModalRappel } from '@/modals/ContentModalRappel/ContentModalRappel';

import styles from './styles.module.css';

type ToastContent = { message: string; description: string };

// La saisie des identifiants et du code MFA se fait sur la page Auth0 (Universal Login) : le bouton
// « Je me connecte » remplace l'ancien formulaire et y redirige.
export const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startLogin } = useAuth0Login();
  const rappel = useRappelConseiller();
  const { windowWidth } = useScreenSize();
  const isMobile = windowWidth <= BREAKPOINTS.mobile;

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showModalRappel, setShowModalRappel] = useState(false);
  const [toast, setToast] = useState<ToastContent | null>(() => {
    if (sessionStorage.getItem(SESSION_EXPIRED_KEY) === 'true') {
      return { message: t('signInForm.sessionExpired'), description: t('signInForm.sessionExpiredDescription') };
    }

    return sessionStorage.getItem(AUTH0_LOGIN_ERROR_KEY) === 'true'
      ? { message: t('signInForm.genericError'), description: '' }
      : null;
  });
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const closeModal = () => {
    setShowModalRappel(false);
  };

  const openModal = () => {
    rappel.mutate();
    document.body.classList.add('modal-open');
    setShowModalRappel(true);
  };

  useEffect(() => {
    // Les messages (session expirée, échec au retour d'Auth0) ne sont affichés qu'une fois
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    sessionStorage.removeItem(AUTH0_LOGIN_ERROR_KEY);
    // Purge des identifiants mémorisés par l'ancien « se souvenir de moi » (désormais géré sur la page Auth0)
    localStorage.removeItem('password');

    void TRACKING_INSTANCE.triggerEvent('page', undefined, {
      event_id: 'page',
      event_categorie: 'affichage espace client',
      event_nom: 'espace client connexion',
      event_page: 'espace client connexion',
    });
  }, []);

  useEffect(() => {
    // Retour arrière depuis Auth0 : Chrome restaure la page depuis le bfcache avec le loader encore affiché
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsRedirecting(false);
      }
    };
    globalThis.addEventListener('pageshow', handlePageShow);

    return () => globalThis.removeEventListener('pageshow', handlePageShow);
  }, []);

  const handleLogin = () => {
    setIsRedirecting(true);
    startLogin().catch(() => {
      setIsRedirecting(false);
      setToast({ message: t('signInForm.genericError'), description: '' });
      trackLoginResult('KO');
    });
  };

  const handleToastClose = () => {
    setToast(null);
  };

  const handlePartnerClick = () => {
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'acceder a mon espace partenaire',
      event_detail_1: 'lien textuel',
    });
    navigate(ROUTE.ACCESPARTENAIRES);
  };

  const handleStockExchangeClick = () => {
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'bourse privee connexion',
      event_detail_1: 'cta',
    });
    window.open(URLS.PRIVATE_STOCK_EXCHANGE_URL, '_blank', 'noopener');
  };

  const benefits = (
    <ul className={styles.benefits}>
      {(['benefitPerformance', 'benefitContracts', 'benefitAdvisor'] as const).map((key) => (
        <li key={key} className={styles.benefit}>
          <MtxIcon name="check" size={16} />
          {t(`loginAccess.${key}`)}
        </li>
      ))}
    </ul>
  );

  const loginForm = (
    <div>
      <div className={styles.buttonConnexion}>
        <MtxButton onClick={handleLogin} variant="primary" size="large" fullWidth>
          <div>{t('common.signInButton')}</div>
        </MtxButton>
      </div>

      <div className={styles.forgotPassword}>
        <Link
          to={ROUTE.RESETPASSWORD}
          onClick={() =>
            void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
              event_id: 'clic',
              event_categorie: 'clic',
              event_metier: 'espace client placement',
              event_nom: 'mot de passe oublie ?',
              event_detail_1: 'cta',
            })
          }
          className={styles.mtpTextLink}
        >
          {t('signInForm.forgotPassword')}
        </Link>
      </div>
    </div>
  );

  const helpSection = (
    <section className={styles.cardHelpContent}>
      <div className={styles.cardHelpTitle}>
        <div className={styles.cardHelpText}>
          <HeadsetIcon height={24} width={24} color="var(--totem-color-tertiary-900)" /> <p>{t('helpInfo.label')}</p>
        </div>
        <div className={styles.cardHelpDescription}>
          <p>{t('helpInfo.descriptionCard')}</p>
        </div>
      </div>

      <div className="card-beasoin-aide-button">
        <MtxButton
          className="card-button-chat-indisponible"
          variant="tertiary"
          size="medium"
          fullWidth={!isMobile}
          onClick={() => {
            openModal();
            void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
              event_id: 'clic',
              event_categorie: 'clic',
              event_metier: 'espace client placement',
              event_nom: 'echanger avec un conseiller',
              event_detail_1: 'cta',
            });
          }}
        >
          <div>{t('helpInfo.chatButton')}</div>
        </MtxButton>
        <MtxButton
          className="card-button-chat-disponible"
          style={{ display: 'none' }}
          variant="tertiary"
          size="medium"
          fullWidth={!isMobile}
          onClick={() => {
            void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
              event_id: 'clic',
              event_categorie: 'clic',
              event_metier: 'espace client placement',
              event_nom: 'echanger avec un conseiller',
              event_detail_1: 'cta',
            });
          }}
        >
          <div>{t('helpInfo.chatButton')}</div>
        </MtxButton>
      </div>
    </section>
  );

  return (
    <MtxGrid className={styles.loginMain} fullWidth>
      {isRedirecting && <Loader />}

      {toast && <Toast message={toast.message} description={toast.description} onClose={handleToastClose} />}

      {showModalRappel && (
        <ModaleDefaut
          content={<ModalRappel onClose={closeModal} data={rappel.data} />}
          onClickClose={closeModal}
          size="large"
          mobileStyle="defaut"
        />
      )}

      <MtxGrid row>
        <MtxGrid col colLg={12} colMd={12} colSm={12} colXs={12}>
          <section className={styles.section}>
            {isMobile ? (
              <AccessAccordion
                id="placement"
                title={t('loginAccess.mainTitle')}
                subtitle={t('loginAccess.mainSubtitle')}
                icon="user"
                isOpen={isAccordionOpen}
                onToggle={() => setIsAccordionOpen(!isAccordionOpen)}
              >
                <div className={styles.accordionContent}>
                  {benefits}
                  {loginForm}
                </div>
              </AccessAccordion>
            ) : (
              <section className={styles.loginCard}>
                <div className={styles.loginCardHeader}>
                  <h1 className={styles.loginCardTitle}>
                    {t('loginAccess.mainTitle')}
                    <br />
                    {t('loginAccess.mainSubtitle')}
                  </h1>
                  {benefits}
                </div>
                {loginForm}
              </section>
            )}

            <div className={styles.cardsColumn}>
              {isMobile ? (
                <LinkCard
                  title={t('loginAccess.partnerTitle')}
                  subtitle={t('loginAccess.partnerSubtitle')}
                  icon="flat"
                  onClick={handlePartnerClick}
                />
              ) : (
                <AccessCard
                  title={t('loginAccess.partnerTitle')}
                  subtitle={t('loginAccess.partnerSubtitle')}
                  icon="flat"
                >
                  <MtxButton onClick={handlePartnerClick} variant="tertiary" size="medium" fullWidth>
                    <div>{t('loginAccess.partnerButton')}</div>
                  </MtxButton>
                </AccessCard>
              )}

              {isMobile ? (
                <LinkCard
                  title={t('loginAccess.stockExchangeTitle')}
                  subtitle={t('loginAccess.stockExchangeSubtitle')}
                  icon="graph"
                  onClick={handleStockExchangeClick}
                />
              ) : (
                <AccessCard
                  title={t('loginAccess.stockExchangeTitle')}
                  subtitle={t('loginAccess.stockExchangeSubtitle')}
                  icon="graph"
                >
                  <MtxButton onClick={handleStockExchangeClick} variant="tertiary" size="medium" fullWidth>
                    <div>{t('loginAccess.stockExchangeButton')}</div>
                  </MtxButton>
                </AccessCard>
              )}
            </div>
          </section>
        </MtxGrid>
      </MtxGrid>

      <MtxGrid row>
        <MtxGrid col colLg={10} offsetLg={1} colMd={10} offsetMd={1} colSm={10} offsetSm={1} colXs={12}>
          {helpSection}
        </MtxGrid>
      </MtxGrid>
    </MtxGrid>
  );
};
